import React, {
  createContext,
  useContext,
  useState,
  ReactNode,
  useMemo,
  useCallback,
} from "react";
import translations, { languages } from "../constants/translations";
import { LanguageInfo } from "../types";

const LANGUAGE_STORAGE_KEY = "preferred-language";

export interface TranslationType {
  [languageCode: string]: {
    [key: string]: string;
  };
}

export interface LanguageContextType {
  language: string;
  currentLanguageInfo: LanguageInfo;
  setLanguage: (code: string) => void;
  t: (key: string) => string;
}

const LanguageContext = createContext<LanguageContextType>({
  language: "en",
  currentLanguageInfo: languages[0],
  setLanguage: () => {
    console.warn("setLanguage was called without a LanguageProvider");
  },
  t: (key: string) => key,
});

export const useLanguage = () => useContext(LanguageContext);

interface LanguageProviderProps {
  children: ReactNode;
}

export const LanguageProvider: React.FC<LanguageProviderProps> = ({
  children,
}) => {
  const [language, setLanguage] = useState(() => {
    if (typeof window !== "undefined") {
      const savedLanguage = localStorage.getItem(LANGUAGE_STORAGE_KEY);
      if (
        savedLanguage &&
        languages.some((lang) => lang.code === savedLanguage)
      ) {
        return savedLanguage;
      }
    }
    return "en";
  });

  const currentLanguageInfo = useMemo(() => {
    return languages.find((lang) => lang.code === language) || languages[0];
  }, [language]);

  const t = useCallback(
    (key: string): string => {
      const currentTranslations = translations[language];
      const defaultTranslations = translations.en;
      if (currentTranslations && currentTranslations[key]) {
        return currentTranslations[key];
      }

      if (defaultTranslations && defaultTranslations[key]) {
        if (process.env.NODE_ENV !== "production") {
          console.warn(
            `Translation key "${key}" not found for language "${language}", falling back to "en".`
          );
        }
        return defaultTranslations[key];
      }

      if (process.env.NODE_ENV !== "production") {
        console.error(`Translation key "${key}" not found in any language.`);
      }
      return key;
    },
    [language]
  );
  const handleSetLanguage = useCallback((code: string) => {
    if (languages.some((lang) => lang.code === code)) {
      setLanguage(code);
      if (typeof window !== "undefined") {
        localStorage.setItem(LANGUAGE_STORAGE_KEY, code);
      }
    } else {
      if (process.env.NODE_ENV !== "production") {
        console.warn(`Attempted to set unknown language code: ${code}`);
      }
    }
  }, []);

  const contextValue = useMemo(() => {
    return {
      language,
      currentLanguageInfo,
      setLanguage: handleSetLanguage,
      t,
    };
  }, [language, currentLanguageInfo, handleSetLanguage, t]);

  return (
    <LanguageContext.Provider value={contextValue}>
      {children}
    </LanguageContext.Provider>
  );
};

export default LanguageProvider;
